import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { RouteParams } from '@/router/type';
import PATH_PAGE from '@/router/path';
import { CaretSortIcon, CheckIcon, GearIcon } from '@radix-ui/react-icons';
import { Building2, LayoutDashboard, Settings } from 'lucide-react';
import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDemoMode } from '@/context/DemoModeContext';
import { useDemoOrganizationStore } from '@/store/demo-organization';

const OVERVIEW_ID = 'overview';

const HeaderOrganization = () => {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams<RouteParams>();
  const { isDemoMode } = useDemoMode();

  const organizations = useDemoOrganizationStore((state) => state.organizations);
  const selectedOrgId = useDemoOrganizationStore((state) => state.selectedOrgId);
  const selectOrganization = useDemoOrganizationStore(
    (state) => state.selectOrganization
  );

  const [value, setValue] = React.useState(orgId || selectedOrgId || OVERVIEW_ID);

  // Check if on overview page
  const isOverviewPage = location.pathname.includes('/dashboard/overview');

  const handleSelectOrganization = (currentValue: string) => {
    setValue(currentValue);
    setOpen(false);

    // Handle overview selection
    if (currentValue === OVERVIEW_ID) {
      const basePath = isDemoMode
        ? '/demo/dashboard/overview'
        : '/dashboard/overview';
      navigate(basePath);
      return;
    }

    selectOrganization(currentValue);

    // Navigate to organization overview (user needs to select workspace next)
    const basePath = isDemoMode
      ? `/demo/dashboard/${currentValue}`
      : `/dashboard/${currentValue}`;
    navigate(basePath);
  };

  const handleGoToSettings = (orgIdToNavigate: string) => {
    setOpen(false);
    const settingsPath = isDemoMode
      ? `/demo${PATH_PAGE.dashboard.orgSetting.general(orgIdToNavigate)}`
      : PATH_PAGE.dashboard.orgSetting.general(orgIdToNavigate);
    navigate(settingsPath);
  };

  useEffect(() => {
    // Handle overview page
    if (isOverviewPage) {
      setValue(OVERVIEW_ID);
      return;
    }

    if (orgId) {
      setValue(orgId);
      selectOrganization(orgId);
    }
  }, [orgId, isOverviewPage]);

  // Only render in demo mode for now
  if (!isDemoMode) return null;

  const organizationOptions = organizations.map((org) => ({
    value: org.id,
    label: org.name,
    role: org.role,
  }));

  const currentOrg = organizationOptions.find((org) => org.value === value);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            aria-label="Select Organization"
            className="justify-between"
          >
            {value === OVERVIEW_ID && (
              <LayoutDashboard className="mr-2 h-4 w-4 text-muted-foreground" />
            )}
            {value !== OVERVIEW_ID && (
              <Building2 className="mr-2 h-4 w-4 text-orange-500" />
            )}
            <span className="max-w-[150px] truncate">
              {value === OVERVIEW_ID
                ? 'Overview'
                : currentOrg?.label || 'Select Organization'}
            </span>
            <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="mx-4 max-w-[300px] p-0">
          <Command>
            <CommandInput placeholder="Search organization" className="h-9" />
            <CommandList>
              <CommandEmpty>No Organization found.</CommandEmpty>
              {/* Overview Option */}
              <CommandGroup>
                <CommandItem
                  value={OVERVIEW_ID}
                  onSelect={handleSelectOrganization}
                >
                  <LayoutDashboard className="mr-2 h-4 w-4 text-muted-foreground" />
                  Overview
                  <CheckIcon
                    className={cn(
                      'ml-auto h-4 w-4',
                      value === OVERVIEW_ID ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              </CommandGroup>
              {/* Organization List */}
              <CommandGroup heading="Organizations">
                {organizationOptions.map((org) => (
                  <CommandItem
                    key={org.value}
                    value={org.value}
                    onSelect={handleSelectOrganization}
                    className="flex items-center"
                  >
                    <Building2 className="mr-2 h-4 w-4 text-orange-500" />
                    <span className="flex-1">{org.label}</span>
                    <span className="mr-2 text-xs text-muted-foreground capitalize">
                      {org.role}
                    </span>
                    <CheckIcon
                      className={cn(
                        'h-4 w-4',
                        value === org.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
              {/* Settings for current org */}
              {value !== OVERVIEW_ID && currentOrg && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => handleGoToSettings(value)}
                      className="text-muted-foreground"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      Organization Settings
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default HeaderOrganization;
